import { useMemo } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';
import { Info, Key, KeyRound, ShieldCheck, Users } from 'lucide-react';

import { useUser } from '@/portainer/users/queries/useUser';
import { isPureAdmin } from '@/portainer/users/user.helpers';
import { UserId } from '@/portainer/users/types';
import { useCurrentUser, useIsPureAdmin } from '@/react/hooks/useUser';

import { PageHeader } from '@@/PageHeader';
import { Tab, WidgetTabs, useCurrentTabIndex } from '@@/Widget/WidgetTabs';
import { Alert } from '@@/Alert';

import { DetailsTab } from './DetailsTab';
import { TeamsTab } from './TeamsTab';
import { AccessTab } from './AccessTab';
import { KubeconfigsTab } from './KubeconfigsTab';
import { TokensTab } from './TokensTab';
import { UserHeader } from './UserHeader';

export function ItemView() {
  const { params } = useCurrentStateAndParams();
  const userId: UserId = Number(params.id);

  const viewerIsAdmin = useIsPureAdmin();
  const { user: currentUser } = useCurrentUser();
  const isSelf = currentUser.Id === userId;

  if (!viewerIsAdmin && !isSelf) {
    return (
      <>
        <PageHeader title="User" breadcrumbs={['User']} />
        <div className="row">
          <div className="col-sm-12">
            <Alert color="error" title="Not your account">
              Only an administrator can open another user&apos;s details.
            </Alert>
          </div>
        </div>
      </>
    );
  }

  return (
    <UserDetail userId={userId} viewerIsAdmin={viewerIsAdmin} isSelf={isSelf} />
  );
}

function UserDetail({
  userId,
  viewerIsAdmin,
  isSelf,
}: {
  userId: UserId;
  viewerIsAdmin: boolean;
  isSelf: boolean;
}) {
  const userQuery = useUser(userId);
  const user = userQuery.data;

  const canSeeTokens = isSelf || (!!user && !isPureAdmin(user));

  const tabs: Array<Tab> = useMemo(
    () => [
      {
        name: 'Details',
        icon: Info,
        widget: user ? (
          <DetailsTab user={user} canEdit={viewerIsAdmin} isSelf={isSelf} />
        ) : null,
        selectedTabParam: 'details',
      },
      {
        name: 'Teams',
        icon: Users,
        widget: user ? (
          <TeamsTab
            userId={userId}
            username={user.Username}
            userIsAdmin={isPureAdmin(user)}
            canEdit={viewerIsAdmin}
          />
        ) : null,
        selectedTabParam: 'teams',
      },
      {
        name: 'Access',
        icon: ShieldCheck,
        widget: <AccessTab userId={userId} />,
        selectedTabParam: 'access',
      },
      {
        name: 'Access tokens',
        icon: Key,
        widget: (
          <TokensTab
            userId={userId}
            isSelf={isSelf}
            canSeeTokens={canSeeTokens}
          />
        ),
        selectedTabParam: 'tokens',
      },
      {
        name: 'Kubeconfigs',
        icon: KeyRound,
        widget: <KubeconfigsTab userId={userId} />,
        selectedTabParam: 'kubeconfigs',
      },
    ],
    [user, userId, viewerIsAdmin, isSelf, canSeeTokens]
  );

  const currentTabIndex = useCurrentTabIndex(tabs);

  if (userQuery.isError) {
    return (
      <>
        <PageHeader
          breadcrumbs={[
            { label: 'Users', link: 'portainer.users' },
            String(userId),
          ]}
        />
        <div className="row">
          <div className="col-sm-12">
            <Alert color="error" title="Could not load this user">
              There is no user {userId}, or you are not allowed to see it.
            </Alert>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Users', link: 'portainer.users' },
          user?.Username ?? String(userId),
        ]}
        reload
      />
      <div className="row">
        <div className="col-sm-12">
          <UserHeader
            userId={userId}
            user={user}
            isLoading={userQuery.isLoading}
            viewerIsAdmin={viewerIsAdmin}
            isSelf={isSelf}
            canSeeTokens={canSeeTokens}
            onRefresh={() => userQuery.refetch()}
          />
        </div>
      </div>
      <WidgetTabs tabs={tabs} currentTabIndex={currentTabIndex} />
      {tabs[currentTabIndex].widget}
    </>
  );
}
